import { useEffect, useRef, useState } from "react";
import api, { formatEUR, formatDate, todayISO, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, Sparkles, Receipt, Trash2, Building2,
  Wallet, TrendingUp, CheckCircle2, ImageIcon,
} from "lucide-react";

const CATEGORIES = [
  "Combustible", "Alimentación", "Restauración", "Suministros",
  "Telefonía e internet", "Transporte", "Material de oficina",
  "Alojamiento", "Publicidad", "Servicios profesionales", "Otros gastos",
];

const CAT_COLOR = {
  "Combustible": "bg-rose-100 text-rose-700",
  "Alimentación": "bg-emerald-100 text-emerald-700",
  "Restauración": "bg-amber-100 text-amber-700",
  "Suministros": "bg-blue-100 text-blue-700",
  "Telefonía e internet": "bg-indigo-100 text-indigo-700",
  "Transporte": "bg-cyan-100 text-cyan-700",
  "Material de oficina": "bg-purple-100 text-purple-700",
  "Alojamiento": "bg-pink-100 text-pink-700",
  "Publicidad": "bg-yellow-100 text-yellow-700",
  "Servicios profesionales": "bg-teal-100 text-teal-700",
  "Otros gastos": "bg-slate-100 text-slate-700",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function compressImage(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const emptyForm = {
  date: todayISO(),
  merchant: "",
  merchant_nif: "",
  total: 0,
  base_imponible: 0,
  iva_amount: 0,
  iva_pct: 21,
  category: "Otros gastos",
  description: "",
  image_base64: null,
  image_mime: "image/jpeg",
};

export default function GastosPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [gestOpen, setGestOpen] = useState(false);
  const [gestRange, setGestRange] = useState({ start: "", end: todayISO() });
  const [gestSending, setGestSending] = useState(false);
  const [filterCat, setFilterCat] = useState("todas");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/expenses"),
        api.get("/expenses/summary/quarterly"),
      ]);
      setItems(l.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Sólo se admiten imágenes (JPG, PNG, WEBP)");
      return;
    }
    setScanning(true);
    try {
      const imgB64 = await compressImage(file);
      // Call OCR
      const { data } = await api.post("/expenses/scan", { image_base64: imgB64 });
      setForm({
        ...emptyForm,
        ...data,
        image_base64: imgB64,
        image_mime: "image/jpeg",
        category: data.category || "Otros gastos",
        merchant: data.merchant || "",
        total: data.total || 0,
        base_imponible: data.base_imponible || 0,
        iva_amount: data.iva_amount || 0,
        iva_pct: data.iva_pct || 21,
        date: data.date || todayISO(),
        description: data.description || "",
        merchant_nif: data.merchant_nif || "",
      });
      setReviewOpen(true);
      toast.success("Ticket escaneado. Revisa los datos antes de guardar.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.merchant.trim() || Number(form.total) <= 0) {
      toast.error("Completa al menos comercio y total");
      return;
    }
    setSaving(true);
    try {
      await api.post("/expenses", {
        ...form,
        total: Number(form.total),
        base_imponible: Number(form.base_imponible),
        iva_amount: Number(form.iva_amount),
        iva_pct: Number(form.iva_pct),
      });
      toast.success("Gasto guardado");
      setReviewOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Eliminado");
    load();
  };

  const sendGestoria = async () => {
    if (!gestRange.start || !gestRange.end) {
      toast.error("Selecciona el rango de fechas");
      return;
    }
    setGestSending(true);
    try {
      const { data } = await api.post("/expenses/send-gestoria", gestRange);
      if (data.sent) {
        toast.success(`${data.count} gastos enviados · IVA soportado: ${formatEUR(data.iva_soportado)}`);
        setGestOpen(false);
        load();
      } else {
        toast.error(data.detail || "No se pudo enviar (revisa email de gestoría)");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setGestSending(false);
    }
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-recalculate IVA when total or iva_pct changes
  const recalcFromTotal = (total, iva_pct) => {
    const base = total / (1 + iva_pct / 100);
    return {
      base_imponible: Number(base.toFixed(2)),
      iva_amount: Number((total - base).toFixed(2)),
    };
  };

  const filtered = items.filter((i) => filterCat === "todas" ? true : i.category === filterCat);

  return (
    <div className="space-y-6" data-testid="gastos-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Gastos y tickets
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fotografía cualquier ticket o factura y la app extrae los datos automáticamente.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="file-input"
          />
          <Button
            variant="outline"
            onClick={() => setGestOpen(true)}
            data-testid="send-gestoria-btn"
          >
            <Building2 className="w-4 h-4 mr-1" /> Enviar a gestoría
          </Button>
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={scanning}
            data-testid="upload-file-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> Subir foto
          </Button>
          <Button
            onClick={() => cameraRef.current?.click()}
            disabled={scanning}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="camera-btn"
          >
            {scanning ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Escaneando...</>
            ) : (
              <><Camera className="w-4 h-4 mr-1" /> Escanear ticket</>
            )}
          </Button>
        </div>
      </div>

      {/* Quarter summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0 stat-card">
            <div className="text-xs uppercase tracking-widest text-blue-100">
              {summary.quarter}
            </div>
            <div className="mt-3 text-sm text-blue-100">Total gastos</div>
            <div className="font-display text-3xl font-bold mt-1" data-testid="summary-quarter-total">
              {formatEUR(summary.quarter_total)}
            </div>
            <div className="text-xs text-blue-100 mt-1">
              {summary.quarter_count} ticket{summary.quarter_count === 1 ? "" : "s"}
            </div>
          </Card>
          <Card className="p-5 stat-card">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="mt-4 text-sm text-slate-500">
              IVA soportado {summary.quarter}
            </div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="summary-iva-soportado">
              {formatEUR(summary.quarter_iva_soportado)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Para tu Modelo 303
            </div>
          </Card>
          <Card className="p-5 stat-card">
            <div className="w-10 h-10 rounded-lg bg-amber-50 grid place-items-center">
              <Wallet className="w-5 h-5 text-amber-600" />
            </div>
            <div className="mt-4 text-sm text-slate-500">IVA anual acumulado</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900">
              {formatEUR(summary.year_iva_soportado)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary.year_count} gastos este año
            </div>
          </Card>
        </div>
      )}

      {/* Categories chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilterCat("todas")}
          className={`text-xs px-3 py-1.5 rounded-full transition ${
            filterCat === "todas" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid="filter-todas"
        >
          Todas ({items.length})
        </button>
        {CATEGORIES.map((c) => {
          const count = items.filter((i) => i.category === c).length;
          if (count === 0) return null;
          return (
            <button
              key={c}
              onClick={() => setFilterCat(c)}
              className={`text-xs px-3 py-1.5 rounded-full transition ${
                filterCat === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c} ({count})
            </button>
          );
        })}
      </div>

      {/* List */}
      <Card className="p-5">
        {loading ? (
          <div className="py-10 text-center text-slate-500">Cargando gastos...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="mt-4 font-display font-bold text-lg text-slate-900">
              Escanea tu primer ticket
            </h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              Haz una foto o sube una imagen. La IA extrae fecha, comercio, IVA y total automáticamente.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button
                onClick={() => cameraRef.current?.click()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="empty-camera-btn"
              >
                <Camera className="w-4 h-4 mr-1" /> Hacer foto
              </Button>
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                data-testid="empty-upload-btn"
              >
                <Upload className="w-4 h-4 mr-1" /> Subir foto
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((g) => (
              <div key={g.id} className="py-3 flex items-center gap-3" data-testid={`expense-${g.id}`}>
                <div className="w-11 h-11 rounded-lg bg-slate-100 grid place-items-center shrink-0">
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900 truncate">
                      {g.merchant}
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${CAT_COLOR[g.category] || CAT_COLOR["Otros gastos"]}`}>
                      {g.category}
                    </span>
                    {g.sent_to_gestoria_at && (
                      <span className="text-[10px] text-emerald-700 flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Gestoría
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {formatDate(g.date)} · Base {formatEUR(g.base_imponible)} · IVA {formatEUR(g.iva_amount)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-slate-900">{formatEUR(g.total)}</div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(g.id)}
                  className="text-rose-500 hover:text-rose-700"
                  data-testid={`delete-expense-${g.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Review dialog after scan */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" /> Revisa los datos extraídos
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {form.image_base64 && (
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100 max-h-64 flex items-center justify-center">
                  <img
                    src={form.image_base64}
                    alt="Ticket"
                    className="max-h-64 max-w-full object-contain"
                    data-testid="review-image"
                  />
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <Label>Comercio</Label>
                <Input
                  value={form.merchant}
                  onChange={(e) => setField("merchant")(e.target.value)}
                  data-testid="review-merchant"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>NIF/CIF</Label>
                  <Input
                    value={form.merchant_nif || ""}
                    onChange={(e) => setField("merchant_nif")(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setField("date")(e.target.value)}
                    data-testid="review-date"
                  />
                </div>
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={form.category} onValueChange={setField("category")}>
                  <SelectTrigger data-testid="review-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>Total €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.total}
                    onChange={(e) => {
                      const t = Number(e.target.value) || 0;
                      const { base_imponible, iva_amount } = recalcFromTotal(t, Number(form.iva_pct) || 21);
                      setForm((f) => ({ ...f, total: t, base_imponible, iva_amount }));
                    }}
                    data-testid="review-total"
                  />
                </div>
                <div>
                  <Label>IVA %</Label>
                  <Input
                    type="number"
                    step="1"
                    value={form.iva_pct}
                    onChange={(e) => {
                      const p = Number(e.target.value) || 0;
                      const { base_imponible, iva_amount } = recalcFromTotal(Number(form.total) || 0, p);
                      setForm((f) => ({ ...f, iva_pct: p, base_imponible, iva_amount }));
                    }}
                  />
                </div>
                <div>
                  <Label>IVA €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.iva_amount}
                    onChange={(e) => setField("iva_amount")(Number(e.target.value))}
                    readOnly
                    className="bg-slate-50"
                  />
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  rows={2}
                  value={form.description || ""}
                  onChange={(e) => setField("description")(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancelar</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="review-save-btn"
            >
              {saving ? "Guardando..." : "Guardar gasto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to gestoria dialog */}
      <Dialog open={gestOpen} onOpenChange={setGestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar gastos a la gestoría</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Selecciona el rango de fechas. Se enviará el resumen + las fotos de los tickets adjuntas.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Desde</Label>
              <Input
                type="date"
                value={gestRange.start}
                onChange={(e) => setGestRange({ ...gestRange, start: e.target.value })}
                data-testid="gest-start"
              />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input
                type="date"
                value={gestRange.end}
                onChange={(e) => setGestRange({ ...gestRange, end: e.target.value })}
                data-testid="gest-end"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGestOpen(false)}>Cancelar</Button>
            <Button
              onClick={sendGestoria}
              disabled={gestSending}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="gest-confirm-btn"
            >
              {gestSending ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
